import { useMemo, useState } from "react";

import { useAlertContext, useConfirmationDialogContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { downloadFile } from "@/common/helper/fileDownloadHelper";
import { useTranslation } from "@/translations";

export interface BulkActionFile {
  documentId: string;
  fileName: string;
}

interface UseDocumentBulkActionsConfig<T extends BulkActionFile> {
  /** The currently visible files — the exposed selection is always intersected with them. */
  files: T[];
  deleteFn: (files: T[]) => Promise<unknown>;
  fetchDownloadBlobFn: (file: T) => Promise<Blob>;
  invalidationKeys: unknown[][];
}

/**
 * Multi-select state plus the DropZone bulk actions (confirm-then-delete, sequential download)
 * over any document list. Extracted from the DropZone panel so every document section — panel
 * zones and asset inventory areas alike — shares one implementation and behaves identically.
 */
export function useDocumentBulkActions<T extends BulkActionFile>(config: UseDocumentBulkActionsConfig<T>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlertContext();
  const { showYesNoDialog } = useConfirmationDialogContext();

  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: config.deleteFn,
    onSuccess: (_data, files) => {
      for (const key of config.invalidationKeys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      setSelectedFiles((prev) => {
        const next = new Set(prev);
        files.forEach((f) => next.delete(f.documentId));
        return next;
      });
      if (files.length === 1 && files[0]) {
        showSuccess(t("dropzone.fileList.deleteSuccess", { fileName: files[0].fileName }));
      } else {
        showSuccess(t("dropzone.fileList.bulkDeleteSuccess", { count: files.length }));
      }
    },
    onError: (_error, files) => {
      if (files.length === 1 && files[0]) {
        showError(t("dropzone.fileList.deleteError", { fileName: files[0].fileName }));
      } else {
        showError(t("dropzone.fileList.bulkDeleteError", { count: files.length }));
      }
    },
  });

  const handleToggleSelect = (fileId: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const deselect = (fileId: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
  };

  const handleDelete = (filesToDelete: T[]) => {
    if (filesToDelete.length === 0) {
      return;
    }
    const text = `${t("dropzone.fileList.bulkDeleteConfirmText")}\n\n${filesToDelete.map((f) => `• ${f.fileName}`).join("\n")}`;
    showYesNoDialog(t("dropzone.fileList.bulkDeleteConfirmTitle"), text, (confirmed) => {
      if (confirmed) {
        deleteMutation.mutate(filesToDelete);
      }
    });
  };

  const effectiveSelectedFiles = useMemo(
    () => new Set(config.files.filter((f) => selectedFiles.has(f.documentId)).map((f) => f.documentId)),
    [config.files, selectedFiles]
  );

  const handleBulkDownload = () => {
    const selectedFilesList = config.files.filter((f) => effectiveSelectedFiles.has(f.documentId));
    setIsDownloading(true);
    void (async () => {
      for (const f of selectedFilesList) {
        try {
          const blob = await config.fetchDownloadBlobFn(f);
          downloadFile(blob, f.fileName);
          await new Promise<void>((r) => setTimeout(r, 150));
        } catch {
          showError(t("dropzone.fileList.downloadError"));
        }
      }
    })().finally(() => setIsDownloading(false));
  };

  return {
    selectedFiles: effectiveSelectedFiles,
    handleToggleSelect,
    deselect,
    handleDelete,
    handleBulkDownload,
    isBulkActionPending: isDownloading || deleteMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
